import React, { useEffect } from 'react';
import { getSettingsClass } from '~/utils/helpers';
import transcodingConfig from '../../../data/config/transcoding-config.json';
import { graphql } from 'gatsby';
import PartialCTA from '../partials/cta';

interface TranscodingCalculatorProps {
  block: {
    settings: Settings;
    total_cost_limit: number;
    transcoding_packages: {
      title?: string;
      subtitle?: string;
      resolution_label: string;
    };
    output_codecs: {
      title?: string;
      subtitle?: string;
    };
    video_duration: {
      title?: string;
      subtitle?: string;
      min_duration: number;
      max_duration: number;
    };
  };
}

type PackageType = (typeof transcodingConfig.transcoding_packages)[number];

const TranscodingCalculator: React.FC<TranscodingCalculatorProps> = ({
  block,
}) => {
  const settingsClass = getSettingsClass(block.settings);
  const [selectedPackage, setSelectedPackage] = React.useState(
    transcodingConfig.transcoding_packages[0]!
  );
  const [codecs, setCodecs] = React.useState<string[]>([
    selectedPackage.output_codecs[0]!,
  ]);
  const [videoDuration, setVideoDuration] = React.useState<number>(
    block.video_duration.min_duration
  );
  const [supportedPackages, setSupportedPackages] = React.useState<string[]>([
    ...selectedPackage.resolutions,
  ]);
  const [basePrice, setBasePrice] = React.useState<number>(
    selectedPackage.price
  );

  const [aboveLimit, setAboveLimit] = React.useState(false);

  const costPerMinute = basePrice * codecs.length;
  const totalCost = costPerMinute * videoDuration;

  const handlePackageChange = (item: PackageType) => () => {
    setSelectedPackage(item);
    setBasePrice(item.price);
    setSupportedPackages(item.resolutions);
  };

  const handleVideoDurationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const videoDuration = Number.parseFloat(event.target.value) || 0;
    if (videoDuration < block.video_duration.min_duration) {
      setVideoDuration(block.video_duration.min_duration);
    } else if (videoDuration > block.video_duration.max_duration) {
      setVideoDuration(block.video_duration.max_duration);
    } else {
      setVideoDuration(videoDuration);
    }
  };

  const handleCodecSelection = (codec: string) => () => {
    if (codecs.includes(codec)) {
      if (codecs.length > 1) {
        setCodecs(codecs.filter(item => item !== codec));
      }
    } else {
      setCodecs([...codecs, codec]);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    });
  };

  useEffect(() => {
    setAboveLimit(totalCost > block.total_cost_limit);
  }, [totalCost]);

  return (
    <div
      className={`block__transcoding-calculator mx-auto my-0 block flex w-[80rem] flex-col rounded-xl bg-[#fff] lg:w-[70rem] md:w-[100%] md:rounded-none ${settingsClass}`}
    >
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex gap-4'>
          {block.transcoding_packages.title && (
            <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
              {block.transcoding_packages.title}
            </div>
          )}
          {block.transcoding_packages.subtitle && (
            <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
              {block.transcoding_packages.subtitle}
            </div>
          )}
        </div>
        <div className='scroll flex flex-row gap-4 py-0 md:flex-col sm:py-4'>
          {transcodingConfig.transcoding_packages.map(item => {
            const packageClass =
              selectedPackage?.type === item.type
                ? 'border-orange-gradient after:absolute after:top-[1.2rem] after:right-[1.2rem] after:size-[1.6rem] after:flex after:bg-tick-ok'
                : 'border-gray-gradient';
            return (
              <button
                key={item.type}
                type='button'
                className={`relative flex flex-1 flex-col gap-2 border-2 px-[1.6rem] py-[1.2rem] box-shadow-light border-gradient border-rounded-soft hover:border-orange-gradient ${packageClass}`}
                onClick={handlePackageChange(item)}
                aria-label={`Select ${item.type} package`}
              >
                <img
                  className='block h-[10.7rem] w-[13.5rem] self-center lg:hidden'
                  src={item.image.src}
                  alt={item.image.alt}
                />
                <div className='flex w-full flex-row items-center justify-between lg:flex-col lg:items-start'>
                  <div className='text-[2rem] font-bold leading-none text-text-primary lg:text-[4.5rem] lg:leading-tight'>
                    {item.type}
                  </div>
                  <div
                    className={`text-dark-blue text-[1.4rem] leading-none text-shadow-light before:content-['$'] after:content-['/min'] lg:text-[2.8rem] lg:leading-normal`}
                  >
                    {item.price}
                  </div>
                </div>
                <p className='text-left text-[1.2rem] leading-9 text-text-gray text-shadow-light lg:text-[1.7rem] lg:leading-normal'>
                  {item.text}
                </p>
              </button>
            );
          })}
        </div>
        <div className='flex flex-col gap-4'>
          <p
            className='font-light m-0 mt-4 text-[1.3rem] leading-9 text-menu-title text-shadow-light'
            dangerouslySetInnerHTML={{
              __html: selectedPackage.resolutions_label,
            }}
          />

          <ul className='flex flex-wrap items-center self-stretch overflow-hidden border-rounded-soft'>
            {transcodingConfig.transcoding_resolutions.map(resolution => {
              const resolutionClass = supportedPackages.includes(resolution)
                ? 'text-menu-title before:size-[1.6rem] before:flex before:bg-tick-ok'
                : 'text-text-gray ';
              return (
                <li
                  key={resolution}
                  className={`flex flex-1 items-center justify-center gap-1 border border-solid border-gray-lighter px-[1.6rem] py-[1.2rem] duration-300 ease-in-out ${resolutionClass}`}
                >
                  {resolution}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='output-codecs__info'>
          {block.output_codecs.title && (
            <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
              {block.output_codecs.title}
            </div>
          )}
          {block.output_codecs.subtitle && (
            <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
              {block.output_codecs.subtitle}
            </div>
          )}
        </div>
        <div className='flex flex-row flex-wrap gap-4 lg:flex-col'>
          {transcodingConfig.output_codecs.map(codec => {
            const codecClass = codecs.includes(codec)
              ? 'border-orange-gradient relative after:absolute after:top-[1.2rem] after:right-[1.2rem] after:size-[1.6rem] after:flex after:bg-tick-ok'
              : 'border-gray-gradient';
            return (
              <div
                key={codec}
                role='button'
                className={`flex flex-1 flex-col items-center justify-center gap-1 border-2 border-gray-lighter px-[1.2rem] py-[1.6rem] box-shadow-light border-gradient border-rounded-soft hover:border-orange-gradient ${codecClass}`}
                onClick={handleCodecSelection(codec)}
              >
                <div className='text-[2rem] font-bold leading-8 text-text-primary'>
                  {codec}
                </div>
                <div className='text-[1.4rem] leading-8 text-text-gray text-shadow-light'>{`($${selectedPackage?.price || 0}/min)`}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex flex-row items-center justify-between gap-4 lg:flex-col lg:items-stretch sm:gap-12'>
          <div className='flex-1'>
            {block.video_duration.title && (
              <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
                {block.video_duration.title}
              </div>
            )}
            {block.video_duration.subtitle && (
              <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
                {block.video_duration.subtitle}
              </div>
            )}
          </div>
          <div className='relative flex max-w-[20ch] flex-1 items-end justify-end border border-solid border-gray-lighter px-6 py-6 border-rounded-soft lg:max-w-full lg:items-center'>
            <input
              className='input-native-reset bg-transparent absolute bottom-0 right-0 top-0 h-full w-full pr-[9rem] text-right text-[3.5rem] font-semibold text-menu-primary text-shadow-light border-rounded-soft'
              type='number'
              value={videoDuration}
              onChange={handleVideoDurationChange}
              inputMode='numeric'
              onWheel={e => e.currentTarget.blur()}
            />
            <span className='z-[1] touch-none select-none text-[3.5rem] font-semibold text-menu-primary text-shadow-light'>
              min
            </span>
          </div>
        </div>
        <div className='slidecontainer block lg:hidden'>
          <input
            type='range'
            className='slider'
            min={block.video_duration.min_duration}
            max={block.video_duration.max_duration}
            value={videoDuration}
            onChange={handleVideoDurationChange}
          />
        </div>
      </div>
      <div className='flex content-stretch items-center border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex-1 text-[2.1rem] text-menu-title text-shadow-light'>
          Cost per minute
        </div>
        <div
          className={`after:content-['/ per minute'] mr-2 text-[3.5rem] font-semibold text-menu-primary text-shadow-light`}
        >
          ${formatNumber(costPerMinute)}
        </div>
        {/* <div className={`text-[1.3rem] text-menu-title text-shadow-light`}>
          / per minute
        </div> */}
      </div>
      <div className='flex content-stretch items-center gap-2 p-12 lg:p-8'>
        <div className='flex-1 text-[2.4rem] font-bold leading-[3.52] text-shadow-light md:leading-normal'>
          Total cost
        </div>
        {aboveLimit ? (
          <PartialCTA
            data={{
              cta_text: 'Contact Sales',
              cta_url: '/contact-sales',
              cta_type: 'compact',
              wr_class: 'flex items-center',
            }}
          />
        ) : (
          <div className='flex-1 text-right text-[4.5rem] font-bold text-shadow-light text-orange-gradient md:text-[4rem]'>{`$${formatNumber(totalCost)}`}</div>
        )}
      </div>
    </div>
  );
};

export const transcodingCalculatorFragment = graphql`
  fragment TranscodingCalculatorFragment on Block {
    _template
    settings {
      ...SettingsFragment
    }
    total_cost_limit
    transcoding_packages {
      title
      subtitle
      resolution_label
    }
    output_codecs {
      title
      subtitle
    }
    video_duration {
      title
      subtitle
      min_duration
      max_duration
    }
  }
`;

export default TranscodingCalculator;
